<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { messageTimestamp, dynamicTime } from 'shared/helpers/timeHelper';
import { formatDuration, formatPhone } from 'dashboard/helper/relayVoicePhone';
import {
  getCallAnnotation,
  saveCallAnnotation,
} from 'dashboard/api/relayVoice';
import Button from 'dashboard/components-next/button/Button.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import {
  callRecordingLink,
  callerMessageLink,
} from 'dashboard/api/relayVoiceInbound';
import RecordingPlayer from './RecordingPlayer.vue';

// One call's facts (read-only) and Relay's note on it (editable, every edit kept).
const props = defineProps({
  accountId: { type: [Number, String], required: true },
  call: { type: Object, required: true },
  outcomes: { type: Array, default: () => [] },
  agents: { type: Array, default: () => [] },
  canCall: { type: Boolean, default: false },
});
const emit = defineEmits(['close', 'saved', 'callBack']);
const { t } = useI18n();

const loading = ref(false);
const saving = ref(false);
const message = ref('');
const failed = ref(false);
const edits = ref([]);
const form = ref({
  note: '',
  outcome: '',
  conversation_id: '',
  follow_up_owner_id: '',
});

const customer = computed(
  () =>
    props.call.contact?.name ||
    (props.call.direction === 'local'
      ? t('RELAY_VOICE.CALLS.INTERNAL', { extension: props.call.number })
      : formatPhone(props.call.number) || t('RELAY_VOICE.UNKNOWN_CALLER'))
);
const facts = computed(() => [
  {
    label: t('RELAY_VOICE.CALLS.STARTED'),
    value: props.call.started_at
      ? messageTimestamp(
          Date.parse(props.call.started_at) / 1000,
          'MMM d, yyyy h:mm a'
        )
      : '—',
  },
  {
    label: t('RELAY_VOICE.CALLS.COLUMNS.RESULT'),
    value: t(`RELAY_VOICE.OUTCOMES.${props.call.outcome}`),
  },
  {
    label: t('RELAY_VOICE.CALLS.TALK_TIME'),
    value: formatDuration(props.call.talk_seconds),
  },
  {
    label: t('RELAY_VOICE.CALLS.HOLD_TIME'),
    value: formatDuration(props.call.hold_seconds),
  },
  {
    label: t('RELAY_VOICE.CALLS.COLUMNS.AGENT'),
    value: props.call.relay_agent?.name || props.call.agent?.name || '—',
  },
  {
    label: t('RELAY_VOICE.CALLS.CAUSE'),
    value: props.call.hangup_cause || '—',
  },
  ...(props.call.wait_seconds !== null && props.call.wait_seconds !== undefined
    ? [
        {
          label: t('RELAY_VOICE.CALLS.WAIT_TIME'),
          value: formatDuration(props.call.wait_seconds),
        },
      ]
    : []),
]);
const recordingLink = download =>
  callRecordingLink(props.accountId, props.call.id, download);
const messageLink = download =>
  callerMessageLink(props.accountId, props.call.id, download);
const isPhone = computed(() => /^\+?\d{10,15}$/.test(props.call.number || ''));
const fieldName = field => t(`RELAY_VOICE.CALLS.FIELDS.${field}`);

const load = async () => {
  loading.value = true;
  message.value = '';
  try {
    const result = await getCallAnnotation(props.accountId, props.call.id);
    const data = result.data || {};
    form.value = {
      note: data.note || '',
      outcome: data.outcome || '',
      conversation_id:
        data.conversation_id || props.call.contact?.conversation_id || '',
      follow_up_owner_id: data.follow_up_owner_id || '',
    };
    edits.value = result.edits;
  } finally {
    loading.value = false;
  }
};
const save = async () => {
  saving.value = true;
  message.value = '';
  try {
    const result = await saveCallAnnotation(props.accountId, props.call.id, {
      ...form.value,
      contact_id: props.call.contact?.contact_id || null,
    });
    edits.value = result.edits;
    failed.value = false;
    message.value = t('RELAY_VOICE.CALLS.SAVED');
    emit('saved', result.data);
  } catch {
    failed.value = true;
    message.value = t('RELAY_VOICE.CALLS.SAVE_ERROR');
  } finally {
    saving.value = false;
  }
};
watch(() => props.call.id, load, { immediate: true });
</script>

<template>
  <aside
    class="flex flex-col w-full sm:w-[26rem] h-full bg-n-surface-1 border-s border-n-weak overflow-y-auto"
    :aria-label="t('RELAY_VOICE.CALLS.DETAILS')"
    @keydown.esc="emit('close')"
  >
    <header class="flex items-start gap-3 p-5 border-b border-n-weak">
      <div class="flex-1 min-w-0">
        <p class="text-xs text-n-slate-10">
          {{ t(`RELAY_VOICE.DIRECTIONS.${call.direction || 'outbound'}`) }}
        </p>
        <h2 class="text-lg font-semibold text-n-slate-12 truncate">
          {{ customer }}
        </h2>
        <p v-if="call.contact" class="text-sm text-n-slate-11 tabular-nums">
          {{ formatPhone(call.number) }}
        </p>
      </div>
      <Button
        v-if="canCall && isPhone"
        :label="t('RELAY_VOICE.CALLS.CALL_BACK')"
        icon="i-ph-phone-bold"
        size="sm"
        color="teal"
        @click="emit('callBack', call.number)"
      />
      <Button
        icon="i-ph-x-bold"
        variant="ghost"
        color="slate"
        size="sm"
        :aria-label="t('RELAY_VOICE.CALLS.CLOSE_DETAILS')"
        @click="emit('close')"
      />
    </header>

    <dl class="grid grid-cols-2 gap-x-4 gap-y-3 p-5 border-b border-n-weak">
      <div v-for="fact in facts" :key="fact.label" class="min-w-0">
        <dt class="text-xs text-n-slate-10">{{ fact.label }}</dt>
        <dd class="text-sm text-n-slate-12 truncate tabular-nums">
          {{ fact.value }}
        </dd>
      </div>
    </dl>

    <section
      v-if="call.has_recording || call.message"
      class="flex flex-col gap-4 p-5 border-b border-n-weak"
      data-tour="calls-recording"
    >
      <RecordingPlayer
        v-if="call.has_recording"
        :key="`call-${call.id}`"
        :fetch-link="recordingLink"
        :label="t('RELAY_VOICE.RECORDING.CALL')"
      />
      <RecordingPlayer
        v-if="call.message?.audio"
        :key="`message-${call.id}`"
        :fetch-link="messageLink"
        :label="
          t(`RELAY_VOICE.RECORDING.MESSAGE_${call.message.kind.toUpperCase()}`)
        "
      />
    </section>

    <div v-if="loading" class="flex justify-center p-8"><Spinner /></div>
    <form
      v-else
      class="flex flex-col gap-4 p-5"
      data-tour="calls-notes"
      @submit.prevent="save"
    >
      <h3 class="text-sm font-semibold text-n-slate-12">
        {{ t('RELAY_VOICE.CALLS.NOTES') }}
      </h3>
      <div
        class="flex flex-wrap gap-1.5"
        role="radiogroup"
        :aria-label="t('RELAY_VOICE.CALLS.OUTCOME_TAG')"
      >
        <button
          v-for="tag in ['', ...outcomes]"
          :key="tag || 'none'"
          type="button"
          role="radio"
          :aria-checked="form.outcome === tag"
          class="px-2.5 py-1 rounded-full text-xs font-medium border transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
          :class="
            form.outcome === tag
              ? 'bg-n-slate-12 text-n-solid-1 border-n-slate-12'
              : 'border-n-weak text-n-slate-11 hover:bg-n-alpha-2'
          "
          @click="form.outcome = tag"
        >
          {{
            tag
              ? t(`RELAY_VOICE.CALLS.TAGS.${tag}`)
              : t('RELAY_VOICE.CALLS.OUTCOME_NONE')
          }}
        </button>
      </div>
      <TextArea
        v-model="form.note"
        :placeholder="t('RELAY_VOICE.CALLS.NOTE_PLACEHOLDER')"
        :max-length="5000"
        auto-height
        min-height="6rem"
      />
      <label class="flex flex-col gap-1 text-xs text-n-slate-11">
        {{ t('RELAY_VOICE.CALLS.CONVERSATION') }}
        <input
          v-model="form.conversation_id"
          inputmode="numeric"
          :placeholder="t('RELAY_VOICE.CALLS.CONVERSATION_PLACEHOLDER')"
          class="!mb-0 rounded-lg border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm text-n-slate-12"
        />
      </label>
      <label class="flex flex-col gap-1 text-xs text-n-slate-11">
        {{ t('RELAY_VOICE.CALLS.FOLLOW_UP_OWNER') }}
        <select
          v-model="form.follow_up_owner_id"
          class="!mb-0 rounded-lg border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm text-n-slate-12"
        >
          <option value="">{{ t('RELAY_VOICE.CALLS.NOBODY') }}</option>
          <option v-for="agent in agents" :key="agent.id" :value="agent.id">
            {{ agent.name }}
          </option>
        </select>
      </label>
      <div class="flex items-center gap-3">
        <Button
          type="submit"
          :label="t('RELAY_VOICE.CALLS.SAVE')"
          :is-loading="saving"
        />
        <p
          v-if="message"
          class="text-xs"
          :class="failed ? 'text-n-ruby-11' : 'text-n-teal-11'"
          :role="failed ? 'alert' : 'status'"
        >
          {{ message }}
        </p>
      </div>

      <section class="flex flex-col gap-2 pt-2 border-t border-n-weak">
        <h3
          class="text-xs font-semibold uppercase tracking-wide text-n-slate-10 pt-3"
        >
          {{ t('RELAY_VOICE.CALLS.EDIT_HISTORY') }}
        </h3>
        <p v-if="!edits.length" class="text-xs text-n-slate-10">
          {{ t('RELAY_VOICE.CALLS.NO_EDITS') }}
        </p>
        <ol v-else class="flex flex-col gap-2">
          <li
            v-for="edit in [...edits].reverse()"
            :key="edit.created_at"
            class="text-xs text-n-slate-11"
          >
            {{
              t('RELAY_VOICE.CALLS.EDIT_ENTRY', {
                name: edit.user_name,
                fields: Object.keys(edit.changed_fields)
                  .map(fieldName)
                  .join(', '),
              })
            }}
            <span class="text-n-slate-10 ms-1">
              {{ dynamicTime(Date.parse(edit.created_at) / 1000) }}
            </span>
          </li>
        </ol>
      </section>
    </form>
  </aside>
</template>
